"use client";

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import { AlertCircle, Check, Clock } from "lucide-react";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";
import type { ProgressIndicatorProps, ProgressStep } from "./types";

export function HorizontalProgressStep({
	steps,
	currentIndex,
	errorIndex,
	className,
	onStepClick,
}: ProgressIndicatorProps) {
	const getStepStatus = (index: number) => {
		if (errorIndex !== undefined && index === errorIndex) return "error";
		if (index < currentIndex) return "completed";
		if (index === currentIndex) return "current";
		return "pending";
	};

	const getLineProgress = () => {
		if (errorIndex !== undefined) {
			return (errorIndex / (steps.length - 1)) * 100;
		}
		if (currentIndex === 0) {
			return 0;
		}
		return Math.min(((currentIndex + 0.5) / (steps.length - 1)) * 100, 100);
	};

	const getLineColor = () => {
		if (errorIndex !== undefined) return "bg-destructive";
		return "bg-muted-foreground/70";
	};

	const getStepIcon = (step: ProgressStep, index: number, status: string) => {
		// If the step has a custom icon, use it
		if (step.icon) return step.icon;

		// Otherwise use default icons based on status
		if (status === "completed") return <Check />;
		if (status === "current") return <Clock />;
		if (status === "error") return <AlertCircle />;
		return (
			<span className="text-sm font-medium text-muted-foreground pointer-events-none">
				{index + 1}
			</span>
		);
	};

	return (
		<div className={cn("w-full", className)}>
			<div className="relative">
				{/* Background Progress Line */}
				<div className="absolute top-4 sm:top-6 left-0 right-0 h-0.5 bg-muted overflow-hidden rounded-full">
					<motion.div
						animate={{ scaleX: getLineProgress() / 100 }}
						className={cn(
							"h-full w-full rounded-full origin-left",
							getLineColor(),
						)}
						initial={{ scaleX: 0 }}
						transition={{ duration: 0.8, ease: "easeInOut" }}
					/>
				</div>

				<div
					className={cn(
						"flex justify-between",
						steps.length < 2 && "justify-center",
					)}
				>
					{steps.map((step, index) => {
						const status = getStepStatus(index);
						const stepIcon = getStepIcon(step, index, status);
						return (
							<motion.div
								animate={{ opacity: 1, y: 0 }}
								className={cn(
									"flex flex-col items-center group",
									onStepClick && "cursor-pointer",
								)}
								data-status={status}
								initial={{ opacity: 0, y: 20 }}
								key={step.id}
								onClick={() => onStepClick?.(index)}
								transition={{ delay: index * 0.1, duration: 0.4 }}
							>
								<motion.div
									className={cn(
										"size-8 sm:size-10 rounded-full border-4 flex items-center justify-center relative z-10 transition-[colors,border-color,background-color,box-shadow] duration-300 bg-background shadow-lg isolate",
										"[&_svg]:size-3 sm:[&_svg]:size-4.5 [&_img]:size-3 sm:[&_img]:size-4.5",
										// Completed state
										"data-[status=completed]:border-success data-[status=completed]:bg-success data-[status=completed]:text-success-foreground data-[status=completed]:shadow-success/20",
										// Current state
										"data-[status=current]:border-accent/10 data-[status=current]:bg-accent data-[status=current]:text-accent-foreground data-[status=current]:shadow-accent/20",
										// Error state
										"data-[status=error]:border-destructive/10 data-[status=error]:bg-destructive-foreground data-[status=error]:text-destructive data-[status=error]:shadow-destructive/20",
										// Pending state
										"data-[status=pending]:border-muted-foreground/20 data-[status=pending]:text-muted-foreground group-hover:data-[status=pending]:border-muted-foreground/40 data-[status=pending]:shadow-muted-foreground/20",
									)}
									data-slot="step-icon"
									data-status={status}
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
								>
									{stepIcon}
								</motion.div>

								<motion.div
									animate={{ opacity: 1 }}
									className="mt-2.5 text-center max-w-32"
									data-slot="step-content"
									initial={{ opacity: 0 }}
									transition={{ delay: index * 0.1 + 0.2 }}
								>
									<h3
										className={cn(
											"text-sm font-medium mb-1 transition-colors duration-300 text-muted-foreground",
											"data-[status=error]:text-destructive",
										)}
										data-status={status}
									>
										{step.title}
									</h3>
									{step.description && (
										<p className="text-xs text-muted-foreground/90 leading-tight">
											{step.description}
										</p>
									)}
									{step.estimatedTime && status === "current" && (
										<p className="hidden sm:block text-xs text-muted-foreground/50 mt-1 font-medium">
											{step.estimatedTime}
										</p>
									)}
								</motion.div>
							</motion.div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
